import { useCallback, useRef, useState } from 'react';
import { generateIdempotencyKey, setPendingIdempotencyKey } from '../utils/idempotencyKey';

/**
 * Guards a raw async submit handler (create/save/void/etc.) against double
 * submission: a synchronous ref blocks re-entrant calls (covers the double-click
 * race a React state re-render can't close in time), and `isPending` is exposed
 * so the caller disables/labels its button the same way `saving`/`voiding` state
 * already does elsewhere in the billing pages.
 *
 * Also stamps every call with a stable Idempotency-Key (see utils/idempotencyKey.js
 * and the axios request interceptor in api/axiosConfig.js) so a resubmission of the
 * same logical request — after a dropped response, or if the double-click guard is
 * ever bypassed — is deduped by the backend idempotency middleware instead of
 * creating a second record. The key is only regenerated after a successful call;
 * a failed attempt keeps its key so a retry replays safely.
 */
export default function useGuardedMutation(mutationFn) {
  const [isPending, setIsPending] = useState(false);
  const pendingRef = useRef(false);
  const keyRef = useRef(generateIdempotencyKey());

  const mutate = useCallback(async (...args) => {
    if (pendingRef.current) return undefined;
    pendingRef.current = true;
    setIsPending(true);
    setPendingIdempotencyKey(keyRef.current);
    try {
      const result = await mutationFn(...args);
      keyRef.current = generateIdempotencyKey();
      return result;
    } finally {
      pendingRef.current = false;
      setIsPending(false);
    }
  }, [mutationFn]);

  return { mutate, isPending };
}
